import React, { createContext, useState, FC, useMemo } from "react";
import { benchmarks as apiBenchmarks, apiBenchmarkDates, apiSelectedBenchmarkDates } from "../lib/api/charts";

type Loading = {
  loading: boolean;
  loaded: boolean;
  error?: Error;
};

export type Benchmark = string;

export type BenchmarkDates = {
  [index: string]: { maxdate: Date; mindate?: Date };
};

export type SelectedBenchmarkDates = {
  maxdate: Date;
  mindate: Date;
};

const defaultBenchmarks: string[] = [];
const defaultSelectedBenchmarkDates: SelectedBenchmarkDates = { mindate: new Date("2015-01-01T00:00:00.000Z"), maxdate: new Date("2015-01-01T00:00:00.000Z") };

export type BenchmarkContextState = {
  benchmarks: string[];
  benchmarkDates: BenchmarkDates;
  selectedBenchmarkDates: SelectedBenchmarkDates;
  selected: Benchmark[];
  oemSelected: Benchmark[];
  customSelected: Benchmark[];
  dealerAnalysisSelected: Benchmark[];
  networkAnalysisSelected: Benchmark[];
  selectedBenchmarks: Benchmark[];
  filterBenchmark: Benchmark;
  filteredBenchmarks: Benchmark[];
  loading: Loading;
  hydrate();
  setBenchmark: (benchmark: Benchmark[]) => void;
  setOemBenchmark: (benchmark: Benchmark[]) => void;
  setCustomBenchmark: (benchmark: Benchmark[]) => void;
  setDealerAnalysisBenchmark: (benchmark: Benchmark[]) => void;
  setNetworkAnalysisBenchmark: (benchmark: Benchmark[]) => void;
  setSelectedBenchmarks(benchmarks: Benchmark[]): void;
  setFilterBenchmark(benchmarks: Benchmark): void;
  setFilteredBenchmarks(benchmarks: Benchmark[]): void;
  getSelectedBenchmarkDates(bench: string[]): void;
};

export const BenchmarkContext = createContext<BenchmarkContextState>({
  benchmarks: defaultBenchmarks,
  benchmarkDates: {},
  selectedBenchmarkDates: defaultSelectedBenchmarkDates,
  selected: [],
  oemSelected: [],
  customSelected: [],
  dealerAnalysisSelected: [],
  networkAnalysisSelected: [],
  selectedBenchmarks: [],
  filterBenchmark: "",
  filteredBenchmarks: [],
  loading: { loading: false, loaded: false, error: undefined },
  hydrate: () => {},
  setBenchmark: () => {},
  setOemBenchmark: () => {},
  setCustomBenchmark: () => {},
  setDealerAnalysisBenchmark: () => {},
  setNetworkAnalysisBenchmark: () => {},
  setSelectedBenchmarks: () => {},
  setFilterBenchmark: () => {},
  setFilteredBenchmarks: () => {},
  getSelectedBenchmarkDates: () => {},
});

export const BenchmarkProvider: FC = ({ children }) => {
  const [benchmarks, setBenchmarks] = useState<string[]>(defaultBenchmarks);
  const [benchmarkDates, setBenchmarkDates] = useState<BenchmarkDates>({});
  const [selectedBenchmarkDates, setSelectedBenchmarkDates] = useState<SelectedBenchmarkDates>(defaultSelectedBenchmarkDates);
  const [selected, setSelected] = useState<Benchmark[]>();
  const [oemSelected, setOemSelected] = useState<Benchmark[]>();
  const [customSelected, setCustomSelected] = useState<Benchmark[]>();
  const [dealerAnalysisSelected, setDealerAnalysisSelected] = useState<Benchmark[]>();
  const [networkAnalysisSelected, setNetworkAnalysisSelected] = useState<Benchmark[]>();
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<Benchmark[]>([]);
  const [filterBenchmark, setFilterBenchmark] = useState<Benchmark>("");
  const [filteredBenchmarks, setFilteredBenchmarks] = useState<Benchmark[]>([]);
  const [loading, setLoading] = useState<Loading>({ loading: false, loaded: false, error: undefined });

  const value = useMemo(
    () => ({
      benchmarks,
      benchmarkDates,
      selectedBenchmarkDates,
      selected,
      oemSelected,
      customSelected,
      dealerAnalysisSelected,
      networkAnalysisSelected,
      selectedBenchmarks,
      filterBenchmark,
      filteredBenchmarks,
      loading,
      setLoading,
      setBenchmark: setSelected,
      setBenchmarkDates,
      setOemBenchmark: setOemSelected,
      setCustomBenchmark: setCustomSelected,
      setDealerAnalysisBenchmark: setDealerAnalysisSelected,
      setNetworkAnalysisBenchmark: setNetworkAnalysisSelected,
      setSelectedBenchmarks,
      setFilterBenchmark,
      setFilteredBenchmarks,
      hydrate: async () => {
        try {
          setLoading({
            loading: true,
            loaded: false,
            error: null,
          });
          const { benchmarks } = await apiBenchmarks();
          const loadedDates = await apiBenchmarkDates();
          const selectedDates = await apiSelectedBenchmarkDates(benchmarks);

          setBenchmarks(benchmarks);
          setFilteredBenchmarks(benchmarks);
          setBenchmarkDates(
            loadedDates.reduce((memo, value) => {
              memo[value.benchmark_name] = { maxdate: value.maxdate };
              return memo;
            }, {}),
          );
          setSelectedBenchmarkDates({ maxdate: new Date(selectedDates[0].maxdate), mindate: new Date(selectedDates[0].mindate) });
          //setSelected();
          setLoading({
            loading: false,
            loaded: true,
            error: null,
          });
        } catch (e) {
          setLoading({
            loading: false,
            loaded: false,
            error: e,
          });
        }
      },
      getSelectedBenchmarkDates: async (bench: string[]) => {
        try {
          setLoading({
            loading: true,
            loaded: false,
            error: null,
          });
          const loadedDates = await apiSelectedBenchmarkDates(bench || benchmarks);

          setSelectedBenchmarkDates({ maxdate: new Date(loadedDates[0].maxdate), mindate: new Date(loadedDates[0].mindate) });
          setLoading({
            loading: false,
            loaded: true,
            error: null,
          });
        } catch (e) {
          setLoading({
            loading: false,
            loaded: false,
            error: e,
          });
        }
      },
    }),
    [
      benchmarks,
      selected,
      oemSelected,
      customSelected,
      dealerAnalysisSelected,
      networkAnalysisSelected,
      selectedBenchmarks,
      filterBenchmark,
      filteredBenchmarks,
      loading,
      setBenchmarks,
      setOemSelected,
      setCustomSelected,
      setDealerAnalysisSelected,
      setNetworkAnalysisSelected,
      setSelected,
      setSelectedBenchmarks,
      setFilterBenchmark,
      setFilteredBenchmarks,
    ],
  );

  return <BenchmarkContext.Provider value={value}>{children}</BenchmarkContext.Provider>;
};
